/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2013, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 * VisRenderSupport
 * INTENT: VisPropertySupport - implements the portion of the VisAPI that is focused on properties and the property manager.
 */

define([
	'underscore',
	'../../../../lib/@waca/core-client/js/core-client/ui/core/Events',
	'../../../../util/ColorUtils',
	'../../../../visualizations/vipr/VIPRUtils'
], function(
	_,
	Events,
	ColorUtils,
	VIPRUtils
) {
	'use strict';

	const STORYTELLING_EFFECT_DURATION = 350;

	var VisPropertySupport = Events.extend({

		init: function(attributes) {
			//Initialize a minimal set of attributes (nested collections from spec etc)
			VisPropertySupport.inherited('init', this, arguments);
			//Assign all attributes to members of this object (to keep old behaviour.)
			this.properties = attributes.properties;
			this.visModelManager = attributes.visModelManager;
			this.dashboardApi = attributes.dashboardApi;
			this.content = attributes.content;
			this.colorsService = this.dashboardApi.getFeature('Colors');
		},

		remove: function() {
			// cleanup external events if needed
		},

		getProperties: function() {
			return this.properties;
		},
		getDefinition: function() {
			return this.visModelManager.getDefinition();
		},

		/**
		 * Try to merge new properties with existing ones
		 * keep the values from the properties with same id and type
		 * create a mapping of current viz properties to their respective values
		 * @param definition: properties of the new vis definition as defined in the smart-vis service
		 *                   (see: smart-viz/ws/src/main/webapp/WEB-INF/localGallery)
		 */
		buildProperties: function(propDefs, options) {
			let propertiesDefs = propDefs;
			if(this.dashboardApi.getType() === 'story'){
				propertiesDefs = this._initializeEntryDurationForStory(propDefs);
			}
			this.getProperties().applyDefaults(propertiesDefs, options);
		},
		/**
		 * Overwrites the property default entry animation to STORYTELLING_EFFECT_DURATION
		 * Because storytelling needs a minimum entry animation duration for it to be noticeable while revealing the widget
		 * @param {Object} propertiesDefs properties of the new vis definition as defined in the smart-vis service
		 * @returns {Object} - the modified properties
		 */
		_initializeEntryDurationForStory: function(propertiesDefs = []) {
			return propertiesDefs.map( propDef => {
				const isEntryDurationID = propDef.id && propDef.id.indexOf('effect.entry') >=0 && propDef.id.indexOf('duration') >=0;
				if(isEntryDurationID && propDef.defaultValue < STORYTELLING_EFFECT_DURATION){
					let propertyDefinition = { ...propDef };
					propertyDefinition.defaultValue = STORYTELLING_EFFECT_DURATION;
					return propertyDefinition;
				}
				return propDef;
			});
		},

		/**
		 * @returns a VisProperty by its id if it is defined for the current viz, null otherwise
		 */
		getPropertyById: function(id) {
			return this.getProperties().get(id);
		},

		/**
		 * @returns true if this property is a color class like 'color11'
		 */
		_isColorClass(colorClass){
			const pattern = new RegExp('color\\d+|grayShade\\d+');
			return pattern.test(colorClass);
		},

		setEffectiveBackgroundColor(fillColor) {
			this.effectiveBackgroundColor = fillColor;
		},

		_getEffectiveBackgroundColor() {
			if (!this.effectiveBackgroundColor) {
				let currentContent = this.content;
				let currentColor = currentContent.getPropertyValue('fillColor');
				// Use default widget backgournd color
				if(!currentColor) {
					currentColor = this.colorsService.getValueForPropertyOfUIElement('widget', 'backgroundColor');
				}

				while (currentColor === 'transparent'){
					// check the parent
					currentContent = currentContent.getContainer();
					if (!currentContent){
						break;
					}
					currentColor = currentContent.getPropertyValue('fillColor') || 'transparent';
				}
				this.effectiveBackgroundColor = currentColor;
			}

			return this.effectiveBackgroundColor;
		},

		/**
		 * @returns the value of the named property as a string, number, boolean depending on the property type.
		 *	Note: This function will return:
		 *		- undefined if the property does not exist in the definiton
		 *      - predefinedValue from viprConfig
		 * 		- vida theming value if the property has vida theming tag
		 *		- default value from viprConfig
		 *		- undefined if there is no default value
		 */
		getPropertyValue: function (id) {
			const visProperty = this.getPropertyById(id);

			if (!visProperty) {
				return undefined;
			}

			let value;
			if(visProperty.isDefault()) {
				value = this._getPropertyDefaultValue(visProperty);
			} else {
				value = visProperty.getValue();
			}

			if (visProperty.isResolveColor() && this._isColorClass(value)) {
				return this.colorsService.getHexColorFromDashboardColorSet(value);
			}

			// Vida doesn't support auto-contrast for axis labels and tiles
			// Live Widget needs to invert the color when:
			// 1. There is user setting value and it's `transparent`
			// or
			// 2. There is no user settings and it would be vida theme token value
			const noUserSettingValue = visProperty.value === undefined && visProperty.predefinedValue == undefined;
			if (value === 'transparent' || noUserSettingValue) {
				// Check if it's theme color property
				const isThemeColorProperty = visProperty.type === 'color' && !!this._getVisPropertyThemeTag(visProperty.tags);
				if (isThemeColorProperty) {
					// Check if it's not a background color property
					const isNotBackgroundColor = !VIPRUtils.getBackGroudColorPropertyIdList().includes(visProperty.id);
					if (isNotBackgroundColor) {
						const effectiveBackgroundColor = this._getEffectiveBackgroundColor();
						const shouldInvertValue = effectiveBackgroundColor === value && effectiveBackgroundColor !== 'transparent';
						if (shouldInvertValue) {
							return this._getInvertColor(effectiveBackgroundColor);
						}
					}
				}
			}

			return value;
		},

		getPropertyDefaultValue(id) {
			const property = this.getPropertyById(id);
			return property && this._getPropertyDefaultValue(property);
		},

		_getPropertyDefaultValue(visProperty) {
			if(visProperty.getPredefinedValue()) {
				return visProperty.getPredefinedValue();
			}
			const visPropertyThemeTag = this._getVisPropertyThemeTag(visProperty.tags);
			if(visPropertyThemeTag) {
				const themeId = this.colorsService.getThemeDefinition().getId();
				const themeDef = VIPRUtils.getVidaThemeDefinition(themeId);
				// Use Vida token theme definition
				if(!_.isEmpty(themeDef)) {
					return themeDef[visPropertyThemeTag];
				}
				// Use the backup theme definition from Theme JSON files
				if(this._hasThemeProperty(visPropertyThemeTag)) {
					return this.colorsService.getThemeDefinition().getValueForPropertyOfUIElement('visualization', visPropertyThemeTag);
				}
			}
			return visProperty.getDefaultValue();
		},


		_getInvertColor: function(bgColorId) {
			const hexColor = this.colorsService.getHexColorFromDashboardColorSet(bgColorId);
			return ColorUtils.invertColor(hexColor);
		},

		_hasThemeProperty(tag) {
			const themePropertyTags = this._getThemePropertyTags();
			return themePropertyTags.includes(tag);
		},

		_getVisPropertyThemeTag(tags = []) {
			return tags.find( tag => tag && tag.substr(0, 1) === '$');
		},

		_getThemePropertyTags() {
			const vidaThemeToken = this.colorsService.getThemeDefinition().getUIElement('visualization');
			return vidaThemeToken.reduce((acc, value) => {
				acc.push(value.id);
				return acc;
			}, []);
		},

		getColorPalettePropertyValue: function() {
			return this.getPropertyValue('colorPalette');
		},

		getConditionalPalettePropertyValue: function() {
			return this.getPropertyValue('condColorPalette');
		},

		/**
		 * @returns a map of VisProperty objects keyed by propertyId for fast lookup.
		 */
		getVisPropertiesAsMap: function() {
			return this.getProperties().modelMap || {};
		},

		_notifyViews: function(eventName, payload) {
			this.visModelManager.trigger(eventName, payload);
		}
	});

	return VisPropertySupport;
});
